<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'
import { useDismissablePopover } from '@/composables/useDismissablePopover'
import { useLocale } from '@/composables/useLocale'

const { t } = useI18n()
const { currentLocale, supportedLocales, switchLocale } = useLocale()
const route = useRoute()

// Docs is active for every /docs/* route (RouterLink prefix matching).
const navLinks = [
  { to: '/for-organizers', label: 'nav.for_organizers' },
  { to: '/for-participants', label: 'nav.for_participants' },
  { to: '/docs', label: 'nav.docs' },
  { to: '/about', label: 'nav.about' },
] as const

// RouterLink only sets aria-current for exact matches; prefix-active links (/docs/*) need it too.
function ariaCurrent(to: string): 'page' | undefined {
  return route.path === to || route.path.startsWith(`${to}/`) ? 'page' : undefined
}

const scrolled = ref(false)

// The mobile menu closes on Escape only: not on outside clicks and not on scroll, so it
// stays open while the page behind the sticky bar scrolls.
const menu = useDismissablePopover({ closeOnEscape: true })
const menuOpen = menu.isOpen

function handleScroll(): void {
  scrolled.value = window.scrollY > 10
}

function toggleMenu(): void {
  menu.toggle()
}

function closeMenu(): void {
  menu.close()
}

// Close the mobile menu whenever the route changes, so a tapped login link
// does not leave the panel open over the destination page.
watch(
  () => route.fullPath,
  () => closeMenu(),
)

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <nav
    class="sticky top-0 z-50 bg-slate-900 transition-all duration-200"
    :class="scrolled ? 'backdrop-blur-sm border-b border-slate-700/60 shadow-lg shadow-black/20' : 'border-b border-transparent'"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <!-- Wordmark — brand name is never translated -->
        <a href="/" class="flex items-center gap-2 group">
          <span class="text-xl font-black text-white tracking-tight group-hover:text-slate-100 transition-colors">
            Startline
          </span>
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 mb-0.5 group-hover:bg-emerald-300 transition-colors"></span>
        </a>

        <!-- Center nav (hidden below lg) -->
        <nav class="hidden lg:flex items-center gap-6" :aria-label="t('nav.primary')">
          <RouterLink
            v-for="link in navLinks"
            :key="link.to"
            :to="link.to"
            :aria-current="ariaCurrent(link.to)"
            class="text-sm font-medium text-slate-300 hover:text-white transition-colors py-1 border-b-2 border-transparent"
            active-class="text-white! border-emerald-400!"
            exact-active-class="text-white! border-emerald-400!"
          >
            {{ t(link.label) }}
          </RouterLink>
        </nav>

        <!-- Right side: language switcher + auth buttons (desktop only, unchanged) -->
        <div class="hidden lg:flex items-center gap-4">
          <!-- Language switcher -->
          <div class="flex items-center gap-1">
            <button
              v-for="loc in supportedLocales"
              :key="loc"
              @click="switchLocale(loc)"
              class="text-xs font-semibold px-2 py-1 rounded transition-colors uppercase"
              :class="currentLocale === loc
                ? 'bg-emerald-500 text-white'
                : 'text-slate-400 hover:text-white'"
              :aria-label="t('common.language') + ': ' + loc.toUpperCase()"
            >
              {{ loc.toUpperCase() }}
            </button>
          </div>

          <!-- Auth buttons -->
          <div class="flex items-center gap-3">
            <RouterLink
              to="/organizer/login"
              class="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-semibold rounded-lg px-5 py-2 text-sm transition-colors"
            >
              {{ t('nav.organizer_login') }}
            </RouterLink>
            <RouterLink
              to="/my/login"
              class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2 text-sm transition-colors"
            >
              {{ t('nav.participant_login') }}
            </RouterLink>
          </div>
        </div>

        <!-- Hamburger toggle (mobile only) -->
        <button
          type="button"
          class="lg:hidden inline-flex items-center justify-center p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
          :aria-label="t('nav.toggle_menu')"
          aria-controls="public-nav-mobile-menu"
          :aria-expanded="menuOpen"
          @click="toggleMenu"
        >
          <svg v-if="!menuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <svg v-else class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Mobile menu panel: expands beneath the sticky bar, overlays page content -->
    <Transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="menuOpen"
        id="public-nav-mobile-menu"
        class="lg:hidden absolute inset-x-0 top-full bg-slate-900 border-b border-slate-700/60 shadow-lg shadow-black/20"
      >
        <div class="px-4 sm:px-6 py-4 space-y-4">
          <!-- Primary links -->
          <nav :aria-label="t('nav.primary')" class="flex flex-col">
            <RouterLink
              v-for="link in navLinks"
              :key="link.to"
              :to="link.to"
              class="text-base font-semibold text-white py-2 border-b border-slate-800"
              active-class="text-emerald-400"
              @click="closeMenu"
            >
              {{ t(link.label) }}
            </RouterLink>
          </nav>

          <!-- Auth links -->
          <div class="flex flex-col gap-3">
            <RouterLink
              to="/organizer/login"
              class="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-semibold rounded-lg px-5 py-2 text-sm text-center transition-colors"
              @click="closeMenu"
            >
              {{ t('nav.organizer_login') }}
            </RouterLink>
            <RouterLink
              to="/my/login"
              class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2 text-sm text-center transition-colors"
              @click="closeMenu"
            >
              {{ t('nav.participant_login') }}
            </RouterLink>
          </div>

          <!-- Language switcher -->
          <div class="pt-3 border-t border-slate-700/60">
            <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
              {{ t('common.language') }}
            </p>
            <div class="flex items-center gap-2">
              <button
                v-for="loc in supportedLocales"
                :key="loc"
                @click="switchLocale(loc)"
                class="text-sm font-semibold px-3 py-1.5 rounded transition-colors uppercase"
                :class="currentLocale === loc
                  ? 'bg-emerald-500 text-white'
                  : 'text-slate-400 hover:text-white bg-slate-800'"
                :aria-label="t('common.language') + ': ' + loc.toUpperCase()"
              >
                {{ loc.toUpperCase() }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </nav>
</template>
